import React, { useState } from 'react';
import { Layout, Menu, Button } from 'antd';
import { 
  HomeOutlined, 
  ToolOutlined, 
  ShoppingOutlined, 
  TeamOutlined, 
  CustomerServiceOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { logout } from '../services/authService';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import '../App.css';

const { Header, Sider, Content, Footer } = Layout;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const menuItems = [
    {
      key: '/home',
      icon: <HomeOutlined />,
      label: t('dashboard'),
    },
    {
      key: '/requests',
      icon: <FileTextOutlined />,
      label: t('requests'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'administration-group',
      type: 'group' as const,
      label: t('administration'),
      children: [
        {
          key: '/equipment',
          icon: <ToolOutlined />,
          label: t('equipment'),
        },
        {
          key: '/consumables',
          icon: <ShoppingOutlined />,
          label: t('consumables'),
        },
        {
          key: '/teams',
          icon: <TeamOutlined />,
          label: t('teams'),
        },
        {
          key: '/services',
          icon: <CustomerServiceOutlined />,
          label: t('services'),
        },
      ],
    },
  ];

  const getAllMenuKeys = (items: any[]): string[] => {
    let keys: string[] = [];
    items.forEach(item => {
      if (item.key && item.key.startsWith('/')) {
        keys.push(item.key);
      }
      if (item.children) {
        keys = keys.concat(getAllMenuKeys(item.children));
      }
    });
    return keys;
  };

  const allKeys = getAllMenuKeys(menuItems);
  const selectedKey = allKeys.find(key => location.pathname.startsWith(key)) || '/home';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        trigger={null}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: '#C8CBC0'
        }}
      >
        <div style={{ 
          height: '64px', 
          margin: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#334E3B',
          fontSize: collapsed ? '14px' : '32px',
          fontWeight: 'bold',
          transition: 'all 0.2s',
          gap: '8px'
        }}>
          <img 
            src={`${process.env.PUBLIC_URL}/favicon.png`}
            alt="DiTasq" 
            style={{ 
              height: collapsed ? '32px' : '40px',
              width: 'auto',
              transition: 'all 0.2s'
            }} 
          />
          {!collapsed && <span >DiTasq</span>}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => handleMenuClick(key)}
          style={{ background: '#C8CBC0', border: 'none' }}
          className="custom-menu"
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'all 0.2s' }}>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 40, height: 40 }}
            />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
              {t('appTitle')}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <LanguageSwitcher />
            <Button 
              type="primary" 
              danger 
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              {t('logout')}
            </Button>
          </div>
        </Header>
        <Content style={{ margin: '24px 16px 0', overflow: 'initial' }}>
          <Outlet />
        </Content>
        <Footer style={{ textAlign: 'center' }}>
          {t('appFooter', { year: new Date().getFullYear() })}
        </Footer>
      </Layout>
    </Layout>
  );
};

export default Home;
